import { IdNamePair, ResidentialUnitDashboardData, ResidentialUnitSpending } from './../types';
import { request } from '@/utils/ajax';
import {
    ResidentialUnit,
    PricingFieldValue,
    BuyerInfo,
    ResidentialUnitExtraData,
    OptionalId,
    ProjectTag,
    ResidentialUnitDeadline,
} from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { BuyerOptionGroupsToUnlinkData } from '@/seller/components/residential-units/ResidentialUnitImport/ResidentialUnitImport';
import {
    InvitationStatus,
    ResidentialUnitImportRow,
    UserSalutation,
    UserTitle,
} from '@ygtechnologies/bygr-frontend-component-library';

export interface ResidentialUnitSearchOptions {
    floors?: string[];
    layoutTypes?: string[];
    tagIds?: number[];
    roomIds?: number[];
    groupIds?: number[];
    searchText?: string;
    sold?: boolean | null;
    hasDeadline?: number[];
    hasUnsentOptions?: boolean | null;
    withBuyerStatus?: InvitationStatus | null;
}
export function getResidentialUnitsByProjectIdWithExtraData(
    projectId: number,
    searchOptions?: ResidentialUnitSearchOptions
): Promise<ResidentialUnitExtraData[]> {
    return request(`api/project/${projectId}/residential-units-extra-data`, {
        query: searchOptions,
    }).then((x) => x.json());
}

export function getResidentialUnitsByProjectId(projectId?: number): Promise<ResidentialUnitExtraData[]> {
    if (!projectId) return Promise.resolve([]);
    return request(`api/project/${projectId}/residential-units`).then((x) => x.json());
}
export function getResidentialUnitExtraData(residentialUnitId: number): Promise<ResidentialUnitExtraData> {
    return request(`api/residential-unit/${residentialUnitId}/extra-data`).then((x) => x.json());
}

export function getResidentialDashboardData(residentialUnitId: number): Promise<ResidentialUnitDashboardData> {
    return request(`api/residential-unit/${residentialUnitId}/dashboard`).then((x) => x.json());
}

export function getResidentialUnit(residentialUnitId: number): Promise<ResidentialUnitExtraData> {
    return request(`api/residential-unit/${residentialUnitId}`).then((x) => x.json());
}

export function getDeadlinesForResidentialUnit(residentialUnitId): Promise<ResidentialUnitDeadline[]> {
    return request(`api/residential-unit/${residentialUnitId}/deadlines`).then((x) => x.json());
}

export function getDeadlineForResidentialUnitAndDeadline(
    residentialUnitId: number,
    deadlineId: number
): Promise<ResidentialUnitDeadline> {
    return request(`api/residential-unit/${residentialUnitId}/deadlines/${deadlineId}`).then((x) => x.json());
}

export function saveResidentialUnitDeadlines(
    residentialUnitId: number,
    deadlines: ResidentialUnitDeadline[]
): Promise<ResidentialUnitDeadline[]> {
    return request(`api/residential-unit/${residentialUnitId}/deadlines`, {
        method: 'PUT',
        json: true,
        body: { deadlines },
    }).then((x) => x.json());
}

function createResidentialUnit(
    residentialUnit: Partial<ResidentialUnit>,
    pricingFieldValues: PricingFieldValue[] | null = null,
    projectTags: OptionalId<ProjectTag>[]
): Promise<ResidentialUnit> {
    return request(`api/residential-unit`, {
        method: 'POST',
        json: true,
        body: { ...residentialUnit, pricingFieldValues, projectTags },
    }).then((x) => x.json());
}

export function updateResidentialUnit(
    residentialUnitId: number,
    residentialUnit: Partial<ResidentialUnit>,
    pricingFieldValues: PricingFieldValue[] | null = null,
    projectTags: OptionalId<ProjectTag>[],
    buyerOptionsGroupIdsToUnlink?: number[]
): Promise<ResidentialUnit> {
    return request(`api/residential-unit/${residentialUnitId}`, {
        method: 'PUT',
        json: true,
        body: { ...residentialUnit, pricingFieldValues, projectTags, buyerOptionsGroupIdsToUnlink },
    }).then((x) => x.json());
}

export function createOrUpdateResidentialUnit(
    residentialUnit: Partial<ResidentialUnit>,
    pricingFieldValues: PricingFieldValue[] | null = null,
    projectTags: OptionalId<ProjectTag>[]
): Promise<ResidentialUnit> {
    return residentialUnit.id
        ? updateResidentialUnit(residentialUnit.id, residentialUnit, pricingFieldValues, projectTags)
        : createResidentialUnit(residentialUnit, pricingFieldValues, projectTags);
}

export function deleteResidentialUnit(residentialUnitId: number) {
    return request(`api/residential-unit/${residentialUnitId}`, {
        method: 'DELETE',
    });
}

export interface InviteBuyerOptions {
    inviteeEmail?: string | null;
    inviteePhoneNumber?: string | null;
    inviteeSalutation: UserSalutation | null;
    inviteeTitle: UserTitle | null;
    inviteeFirstName: string;
    inviteeLastName: string;
    // sendEmail: boolean;
    sendSms: boolean;
}

export function inviteResidentialUnitBuyer(
    residentialUnitId: number,
    invitations: InviteBuyerOptions[],
    shouldInvite?: boolean
) {
    return request(`api/buyer-invitation`, {
        method: 'POST',
        json: true,
        body: { residentialUnitId, invitations, shouldInvite },
    });
}

export interface ResendBuyerInviteOptions {
    sendEmail: boolean;
    sendSms: boolean;
}

export function resendResidentialUnitBuyerInvitation(buyerInvitationId: number, options: ResendBuyerInviteOptions) {
    return request(`api/buyer-invitation/${buyerInvitationId}/resend`, {
        method: 'POST',
        json: true,
        body: options,
    });
}

export function updateResidentialUnitBuyerInvitation(
    residentialUnitId: number,
    buyerInvitationId: number,
    invitation: InviteBuyerOptions,
    shouldInvite?: boolean
) {
    return request(`api/buyer-invitation/${buyerInvitationId}`, {
        method: 'PUT',
        json: true,
        body: { residentialUnitId, ...invitation, sendEmail: true, shouldInvite },
    });
}

export function cancelResidentialUnitBuyerInvitation(buyerInvitationId: number) {
    return request(`api/buyer-invitation/${buyerInvitationId}`, {
        method: 'DELETE',
    });
}

export function removeResidentialUnitBuyer(residentialUnitId: number, applicationUserId: number) {
    return request(`api/residential-unit/${residentialUnitId}/buyers/${applicationUserId}`, {
        method: 'DELETE',
    });
}

export function getResidentialUnitBuyerInfo(residentialUnitId: number): Promise<BuyerInfo> {
    return request(`api/residential-unit/${residentialUnitId}/buyers`).then((x) => x.json());
}

export function parseResidentialUnitImportText(projectId: number, stringData: string): Promise<ResidentialUnitImportRow[]> {
    return request(`api/residential-unit/import/parse/text`, {
        method: 'POST',
        json: true,
        body: { projectId, stringData },
    }).then((x) => x.json());
}

export function parseResidentialUnitImportExcel(projectId: number, file: FileData): Promise<ResidentialUnitImportRow[]> {
    return request(`api/residential-unit/import/parse/excel`, {
        method: 'POST',
        json: true,
        body: { projectId, file },
    }).then((x) => x.json());
}

export function importResidentialUnits(
    projectId: number,
    data: ResidentialUnitImportRow[],
    buyerOptionGroupsToUnlinkData: BuyerOptionGroupsToUnlinkData[]
) {
    return request(`api/residential-unit/import`, {
        method: 'POST',
        json: true,
        body: { projectId, data, buyerOptionGroupsToUnlinkData },
    });
}

export function getResidentialUnitTodo(residentialUnitId) {
    return request(`api/residential-unit/${residentialUnitId}/todo`).then((x) => x.json());
}

export function getResidentialUnitSpending(residentialUnitId): Promise<ResidentialUnitSpending> {
    return request(`api/residential-unit/${residentialUnitId}/spending`).then((x) => x.json());
}

export interface UnitPresetSelection {
    residentialUnitId: number;
    presetSelections: PresetSelection[];
}
export interface PresetSelection {
    presetGroup: IdNamePair;
    selectedPreset: IdNamePair;
}

export function getResidentialUnitPresetSelections(residentialUnitId: number): Promise<UnitPresetSelection> {
    return request(`api/residential-unit/${residentialUnitId}/preset-selections`).then((x) => x.json());
}

export interface BulkUpdateResidentialUnitReadonlyRequest {
    residentialUnitIds: number[];
    isReadOnlyForBuyers: boolean;
}

export function bulkUpdateResidentialUnitReadonly(requestData: BulkUpdateResidentialUnitReadonlyRequest): Promise<void> {
    return request(`api/residential-unit/bulk-update-readonly`, {
        method: 'PUT',
        json: true,
        body: requestData,
    }).then(() => {});
}
